import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, MapPin, Clock, Package, X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: null, label: "All" },
  { id: "clothing", label: "Clothing" },
  { id: "footwear", label: "Footwear" },
  { id: "accessory", label: "Accessories" },
];

export default function Makers() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [makers, setMakers] = useState(null);
  const [filter, setFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [designs, setDesigns] = useState([]);
  const attachDesignId = search.get("design");

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) {
      api.get("/designs")
        .then(({ data }) => setDesigns(data))
        .catch((err) => console.error("Load designs failed:", err));
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    const params = filter ? { specialty: filter } : {};
    setMakers(null);
    api.get("/makers", { params })
      .then(({ data }) => setMakers(data))
      .catch((err) => { console.error("Load makers failed:", err); setMakers([]); });
  }, [filter]);

  useEffect(() => {
    // Auto-open quote form if arriving with a design query param
    if (attachDesignId && makers && makers.length && !selected) {
      // no auto-select maker; user picks
    }
  }, [attachDesignId, makers, selected]);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 pt-24 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-8">
            <div className="overline text-zinc-500 mb-3">§ 03 — The Bureau</div>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9]">Makers, ateliers,<br /><em className="italic font-light">manufacturers.</em></h1>
            <p className="mt-6 max-w-2xl text-lg text-zinc-700 leading-relaxed">
              Don&apos;t sew? Don&apos;t worry. Send any design straight to a vetted independent designer or a small-run manufacturer. They&apos;ll come back with a quote and lead time.
            </p>
            <Link to="/apply-maker" className="mt-6 inline-flex overline items-center gap-2 text-zinc-950 border-b border-zinc-950 hover:text-lime-600 hover:border-lime-600 transition-colors" data-testid="makers-apply-cta">
              Are you a maker? Apply to join →
            </Link>
          </div>
          <div className="md:col-span-4 md:text-right flex md:justify-end items-end">
            <div className="flex flex-wrap gap-1" data-testid="makers-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.label}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 overline border ${filter === f.id ? "bg-zinc-950 text-zinc-50 border-zinc-950" : "border-zinc-300 hover:border-zinc-950"} transition-colors`}
                  data-testid={`makers-filter-${f.label.toLowerCase()}`}
                >{f.label}</button>
              ))}
            </div>
          </div>
        </div>

        {makers === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 border-t border-zinc-200" data-testid="makers-grid">
            {makers.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelected(m)}
                className="text-left group border-r border-b border-zinc-200 hover:bg-lime-400/10 transition-colors p-6"
                data-testid={`maker-card-${m.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden mb-4">
                  <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute top-2 left-2 backdrop-blur bg-zinc-950/60 border border-white/10 text-zinc-50 overline px-2 py-1">{m.tier}</div>
                </div>
                <div className="overline text-zinc-500 mb-1">{m.specialties.join(" · ")}</div>
                <div className="font-serif text-2xl leading-tight">{m.name}</div>
                <div className="mt-2 flex items-center gap-4 text-xs text-zinc-600">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.city}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.lead_time_days}d</span>
                  <span className="flex items-center gap-1"><Package className="w-3 h-3" />MOQ {m.moq}</span>
                  <span className="ml-auto font-mono">{m.price_range}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <MakerDetail
          maker={selected}
          designs={designs}
          attachDesignId={attachDesignId}
          user={user}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function MakerDetail({ maker, designs, attachDesignId, user, onClose }) {
  const [designId, setDesignId] = useState(attachDesignId || designs[0]?.id || "");
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState("M");
  const [deadline, setDeadline] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!designId) { toast.error("Pick a design first"); return; }
    if (!email) { toast.error("Contact email required"); return; }
    setBusy(true);
    try {
      await api.post("/quote-requests", {
        maker_id: maker.id, design_id: designId, quantity, size, deadline: deadline || null, message, contact_email: email,
      });
      setSent(true);
      toast.success("Quote request sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/90 backdrop-blur-md flex items-start md:items-center justify-center overflow-y-auto py-10 px-4" data-testid="maker-detail-modal">
      <div className="w-full max-w-5xl bg-zinc-50 text-zinc-950 border border-zinc-200 relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-zinc-950 text-zinc-50 hover:bg-lime-400 hover:text-zinc-950 z-10" data-testid="maker-close-btn">
          <X className="w-4 h-4" />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="relative aspect-square md:aspect-auto">
            <img src={maker.image} alt={maker.name} className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="p-8 md:p-10">
            <div className="overline text-zinc-500 mb-2">{maker.tier}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95]">{maker.name}</h2>
            <div className="mt-2 flex items-center gap-4 text-xs text-zinc-600 border-b border-zinc-200 pb-4">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{maker.city}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{maker.lead_time_days} days</span>
              <span className="flex items-center gap-1"><Package className="w-3 h-3" />MOQ {maker.moq}</span>
              <span className="ml-auto font-mono">{maker.price_range}</span>
            </div>
            <p className="mt-4 text-zinc-700 leading-relaxed">{maker.bio}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              {maker.tags?.map((t) => <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-300 text-zinc-600">{t}</span>)}
            </div>

            {sent ? (
              <div className="mt-8 p-6 border border-lime-400 bg-lime-400/10 flex items-start gap-3" data-testid="quote-sent-view">
                <CheckCircle2 className="w-6 h-6 text-lime-600 flex-shrink-0" />
                <div>
                  <div className="font-serif text-2xl">Sent to {maker.name}.</div>
                  <p className="text-sm text-zinc-600 mt-1">They&apos;ll reply to <span className="font-mono">{email}</span> with a quote and lead time.</p>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-3" data-testid="quote-form">
                <div>
                  <div className="overline text-zinc-500 mb-1">Attach Design</div>
                  {designs.length === 0 ? (
                    <div className="text-sm text-zinc-500 italic border border-dashed border-zinc-300 p-3">Create a design first to request a quote.</div>
                  ) : (
                    <select value={designId} onChange={(e) => setDesignId(e.target.value)} className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none" data-testid="quote-design-select">
                      {designs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                    </select>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="overline text-zinc-500 mb-1">Qty</div>
                    <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none" data-testid="quote-quantity-input"/>
                  </div>
                  <div>
                    <div className="overline text-zinc-500 mb-1">Size</div>
                    <input value={size} onChange={(e) => setSize(e.target.value)} className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none" data-testid="quote-size-input"/>
                  </div>
                  <div>
                    <div className="overline text-zinc-500 mb-1">Deadline</div>
                    <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none" data-testid="quote-deadline-input"/>
                  </div>
                </div>
                <div>
                  <div className="overline text-zinc-500 mb-1">Your Email</div>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none" data-testid="quote-email-input"/>
                </div>
                <div>
                  <div className="overline text-zinc-500 mb-1">Notes</div>
                  <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Any specifics on fabric, colorway, timeline…" className="w-full bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none resize-none" data-testid="quote-message-input"/>
                </div>
                <button
                  onClick={submit}
                  disabled={busy || designs.length === 0}
                  className={`w-full mt-2 py-3 rounded-full overline flex items-center justify-center gap-2 ${busy || designs.length === 0 ? "bg-zinc-300 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-950 hover:text-lime-400"} transition-colors`}
                  data-testid="quote-submit-btn"
                >
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <><Send className="w-4 h-4" /> Request Quote</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
